import './scrolltable.scss';

class Scrolltable {
    constructor (element, options) {
        const defaults = {
            offset: 4,
            scrollNavigation: true
        };

        this.settings = Object.assign({}, defaults, options);

        this.$table = element;
        this.columnStickyModus = this.$table.dataset.columnStickyModus;

        this.scrollable = false;

        this.scrollStart = true;
        this.scrollEnd = false;
        this.scrolling = true;

        this.currentColumn = 1;
        this.jumpTimer = null;

        this.buildWrapper(() => {
            this.initialize();
        });
    }

    initialize () {
        this.checkScrolltable();
        this.setEvents();
    }

    buildWrapper (callback) {
        const _createElementWithClass = (tag, classname) => {
            const $object = document.createElement(tag);
            $object.classList.add(classname);
            return $object;
        };

        this.$tableCaption = this.$table.querySelector('caption');

        this.$tableTag = this.$table.querySelector('table');
        this.$tableTag.setAttribute('role', 'table');

        if (this.$table.dataset.tableId !== undefined) {
            this.$tableTag.setAttribute('aria-labelledby', `table_caption_${this.$table.dataset.tableId}`);
        } else {
            this.$tableTag.setAttribute('aria-label', window.translations.scrollTable.tableAriaLabel);
        }

        this.$scrollTable = _createElementWithClass('div', 'scrolltable');
        this.$table.parentNode.insertBefore(this.$scrollTable, this.$table);

        this.$scrollTableInner = _createElementWithClass('div', 'scrolltable__inner');
        this.$scrollTable.appendChild(this.$scrollTableInner);

        this.$scrollTableScroller = _createElementWithClass('div', 'scrolltable__scroller');
        this.$scrollTableScroller.setAttribute('tabindex', 0);
        this.$scrollTableScroller.setAttribute('role', 'region');
        this.$scrollTableScroller.setAttribute('aria-label', window.translations.scrollTable.scrollerAriaLabel);
        this.$scrollTableInner.appendChild(this.$scrollTableScroller);

        this.$scrollTableScrollerInner = _createElementWithClass('div', 'scrolltable__scroller-inner');
        this.$scrollTableScroller.appendChild(this.$scrollTableScrollerInner);

        this.$scrollTableScrollerInner.appendChild(this.$table);

        this.$scrollTableControls = _createElementWithClass('div', 'scrolltable__controls');
        this.$scrollTable.appendChild(this.$scrollTableControls);

        this.$scrollTableProgressbar = _createElementWithClass('div', 'scrolltable__progressbar');
        this.$scrollTableControls.appendChild(this.$scrollTableProgressbar);

        this.$scrollTableProgressbarIndicator = _createElementWithClass('div', 'scrolltable__progressbar-indicator');
        this.$scrollTableProgressbar.appendChild(this.$scrollTableProgressbarIndicator);

        this.$scrollTableButtons = _createElementWithClass('div', 'scrolltable__buttons');
        this.$scrollTableControls.appendChild(this.$scrollTableButtons);

        this.$scrollTablePrevButton = _createElementWithClass('button', 'scrolltable__prev');
        this.$scrollTablePrevButton.setAttribute('aria-label', window.translations.scrollTable.scrollPrev);
        this.$scrollTablePrevButton.innerHTML = '<svg width="24" height="24" aria-hidden="true" role="none"><use xlink:href="#icon-arrow-left"></use></svg>';
        this.$scrollTableButtons.appendChild(this.$scrollTablePrevButton);

        this.$scrollTableNextButton = _createElementWithClass('button', 'scrolltable__next');
        this.$scrollTableNextButton.setAttribute('aria-label', window.translations.scrollTable.scrollNext);
        this.$scrollTableNextButton.innerHTML = '<svg width="24" height="24" aria-hidden="true" role="none"><use xlink:href="#icon-arrow-right"></use></svg>';
        this.$scrollTableButtons.appendChild(this.$scrollTableNextButton);

        this.$tableRows = this.$table.querySelectorAll('tr');
        this.$tableFirstRow = this.$table.querySelectorAll('tr')[0];
        this.$tableFirstColumns = this.$tableFirstRow.querySelectorAll('th, td');

        if (typeof callback === 'function') {
            callback();
        }
    }

    checkScrolltable () {
        if (this.$scrollTable.offsetWidth < this.$scrollTableScrollerInner.offsetWidth) {
            this.$scrollTable.classList.add('scrolltable--scroll');
        } else {
            this.$scrollTable.classList.remove('scrolltable--scroll');
        }

        this.checkButtons();
    }

    setEvents () {
        if (this.columnStickyModus === 'true' && this.$tableRows.length > 1) {
            this.$tableRows.forEach($tableRow => {
                const $firstColumnInRow = $tableRow.querySelectorAll('td')[0];

                if ($firstColumnInRow) {
                    $tableRow.style.setProperty('--row-height', $firstColumnInRow.style.height);
                }
            });
        }

        this.$scrollTableScroller.addEventListener('scroll', () => {
            this.onScroll(this.$scrollTableScroller.scrollLeft);
        });

        this.$scrollTablePrevButton.addEventListener('click', () => {
            if (this.scrollStart === false) {
                let column = 0;
                if (this.currentColumn > 0) {
                    column = this.currentColumn - 1;
                }

                this.jumpToColumn(column);
            }
        });

        this.$scrollTableNextButton.addEventListener('click', () => {
            if (this.scrollEnd === false) {
                let column = this.$tableFirstColumns.length;
                if (this.currentColumn < this.$tableFirstColumns.length) {
                    column = this.currentColumn + 1;
                }
                this.jumpToColumn(column);
            }
        });

        window.addEventListener('resize', () => {
            this.checkScrolltable();
        });

        window.addEventListener('orientationchange', () => {
            this.checkScrolltable();
        });
    }

    onScroll (scrollLeft) {
        const scrollDistance = Math.round(this.$scrollTableScrollerInner.offsetWidth - this.$scrollTable.offsetWidth);
        let percentage = Math.round(100 / scrollDistance * scrollLeft);

        if (percentage >= 100) {
            percentage = 100;
            this.scrollEnd = true;
            this.scrollStart = false;
        } else if (percentage === 0) {
            this.scrollStart = true;
            this.scrollEnd = false;
        } else {
            this.scrollStart = false;
            this.scrollEnd = false;
        }

        this.setProgress(percentage, scrollLeft);
        if (this.scrolling === true) {
            this.setCurrentColumn(scrollLeft);
        }
        this.checkButtons();

        if (this.$tableCaption) {
            this.setCaption(scrollLeft);
        }
    }

    setProgress (percentage) {
        const clampedPercentage = Math.max(0, Math.min(percentage, 100));
        this.$scrollTableProgressbarIndicator.style.maxWidth = clampedPercentage + '%';
    }

    setCaption (scrollLeft) {
        this.$tableCaption.style.transform = 'translateX(' + scrollLeft + 'px)';
    }

    checkButtons () {
        if (this.scrollStart === true) {
            this.$scrollTablePrevButton.classList.add('is--inactive');
            this.$scrollTablePrevButton.setAttribute('disabled', 'disabled');
        } else {
            this.$scrollTablePrevButton.classList.remove('is--inactive');
            this.$scrollTablePrevButton.removeAttribute('disabled');
        }

        if (this.scrollEnd === true) {
            this.$scrollTableNextButton.classList.add('is--inactive');
            this.$scrollTableNextButton.setAttribute('disabled', 'disabled');
        } else {
            this.$scrollTableNextButton.classList.remove('is--inactive');
            this.$scrollTableNextButton.removeAttribute('disabled');
        }
    }

    setCurrentColumn (scrollLeft) {
        for (let i = 0; i < this.$tableFirstColumns.length; i++) {
            const $currentColumn = this.$tableFirstColumns[i];
            const currentOffset = parseInt($currentColumn.offsetLeft);
            const $nextColumn = this.$tableFirstColumns[i + 1];

            if (this.currentColumn !== i) {
                if ($nextColumn) {
                    const nextOffset = parseInt($nextColumn.offsetLeft);

                    if (currentOffset < scrollLeft && scrollLeft <= nextOffset) {
                        this.currentColumn = i;
                    }
                } else {
                    if (scrollLeft === currentOffset) {
                        this.currentColumn = i;
                    }
                }
            }
        }
    }

    jumpToColumn (index) {
        const scrollYPosition = this.$scrollTableScroller.scrollTop;
        const scrollXPosition = this.$tableFirstColumns[index].offsetLeft - this.$table.offsetLeft;

        this.scrolling = false;
        this.$scrollTableScroller.scrollTo({
            top: scrollYPosition,
            left: scrollXPosition,
            behavior: 'smooth'
        });
        this.currentColumn = index;

        clearTimeout(this.jumpTimer);
        this.jumpTimer = window.setTimeout(() => {
            this.scrolling = true;
        }, 500);
    }
}

export { Scrolltable };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    const $$scrolltable = $context.querySelectorAll('[data-scrolltable="root"]');

    for (let i = 0; i < $$scrolltable.length; i++) {
        const $scrolltable = $$scrolltable[i];
        const scrolltableAPI = new Scrolltable($scrolltable);
        $scrolltable.scrolltableAPI = scrolltableAPI;
    }
});
